import { createI18n as createClientI18n, type I18n } from 'vue-i18n'
import { LanguageType } from '@/constants/language'

type LocaleFile = Record<string, any>
export type LanguageResources = Record<string, () => Promise<any>>
type Messages = { [key in LanguageType]?: LocaleFile }

export interface IResourceLoader {
    getResourceAsync: (language: LanguageType) => Promise<LocaleFile>
}

export class ResourceLoader implements IResourceLoader {
    private readonly resources: LanguageResources

    constructor(resources: LanguageResources = {}) {
        this.resources = resources
    }

    async getResourceAsync(language: LanguageType): Promise<LocaleFile> {
        const extractLocSource = (obj: any, currentPath: string[] = []): { [key: string]: string } => {
            const result: { [key: string]: string } = {}

            for (const key in obj) {
                const value = obj[key]
                const fullKey = [...currentPath, key].join('.')

                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    // 如果是對象，則遞歸處理
                    Object.assign(result, extractLocSource(value, [...currentPath, key]))
                }
                else {
                    // 如果不是對象（包括字符串、數字等），則直接賦值
                    result[fullKey] = value
                }
            }

            return result
        }

        const searchKey = language.toUpperCase().replace('_', '-')
        let resource = {}

        for (const key of Object.keys(this.resources)) {
            if (key.toUpperCase().includes(searchKey)) {
                const module = JSON.parse((await this.resources[key]()).default)

                Object.assign(resource, extractLocSource(module))

                resource = { ...resource, ...module.default }
            }
        }

        return resource
    }
}

export class I18nFactory {
    static async createDefault(resources: LanguageResources, locale: LanguageType = LanguageType.EN) {
        const resourceLoader = new ResourceLoader(resources)
        const messages: Messages = {}
        messages[LanguageType.EN] = await resourceLoader.getResourceAsync(LanguageType.EN)

        if (locale !== LanguageType.EN) {
            messages[locale] = await resourceLoader.getResourceAsync(locale)
        }
        return createClientI18n({
            legacy: false,
            globalInjection: true,
            warnHtmlMessage: false,
            locale,
            fallbackLocale: LanguageType.EN,
            messages,
        })
    }

    static async create(
        defaultResource: LocaleFile,
        resources: LanguageResources,
        locale: LanguageType = LanguageType.EN,
    ) {
        const messages: Messages = {}
        messages[LanguageType.EN] = defaultResource
        const resourceLoader = new ResourceLoader(resources)

        if (locale !== LanguageType.EN) {
            messages[locale] = await resourceLoader.getResourceAsync(locale)
        }
        return createClientI18n({
            legacy: false,
            globalInjection: true,
            warnHtmlMessage: false,
            locale,
            fallbackLocale: LanguageType.EN,
            messages,
        })
    }
}

export interface IResourceManagement {
    updateLanguageAsync: (language: LanguageType) => Promise<void>
    getI18nInstance: () => I18n
}
export class ResourceManagementFactory {
    static async createResourceManagementAsync(
        resources: LanguageResources,
        locale: LanguageType,
    ): Promise<IResourceManagement> {
        const resourceLoader = new ResourceLoader(resources)
        const messages: Messages = {}
        messages[LanguageType.EN] = await resourceLoader.getResourceAsync(LanguageType.EN)

        if (locale !== LanguageType.EN) {
            messages[locale] = await resourceLoader.getResourceAsync(locale)
        }

        const i18n: I18n = createClientI18n({
            legacy: false,
            globalInjection: true,
            warnHtmlMessage: false,
            locale,
            fallbackLocale: LanguageType.EN,
            messages,
        })

        return new ResourceManagement({
            i18n,
            resourceLoader,
        })
    }
}
interface ResourceManagementOptions {
    i18n: I18n
    resourceLoader: IResourceLoader
}

export class ResourceManagement implements IResourceManagement {
    private readonly i18n: I18n
    private readonly resourceLoader: IResourceLoader

    constructor({ i18n, resourceLoader }: ResourceManagementOptions) {
        this.i18n = i18n
        this.resourceLoader = resourceLoader
    }

    async updateLanguageAsync(language: LanguageType): Promise<void> {
        if (!this.i18n.global.availableLocales.includes(language)) {
            const languageResource = await this.resourceLoader.getResourceAsync(language)
            this.i18n.global.setLocaleMessage(language, languageResource)
        }
        // @ts-expect-error global locale is ref interfaces
        this.i18n.global.locale.value = language
    }

    getI18nInstance() {
        return this.i18n
    }
}
